import {Component, inject, Input, OnChanges, OnDestroy, OnInit} from '@angular/core';
import {SearchContainer} from "../../../../core/definitions/search-container";
import {FilterGroupHierarchyNode, HierarchicFilterGroup} from "../../../../core/definitions/search-objects";
import {
  MatTree,
  MatTreeFlatDataSource,
  MatTreeFlattener,
  MatTreeNode,
  MatTreeNodeDef,
  MatTreeNodePadding, MatTreeNodeToggle
} from "@angular/material/tree";
import {MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {FlatTreeControl} from "@angular/cdk/tree";
import {NgClass} from "@angular/common";
import { SearchExecutorService } from 'src/app/object-search/search-executor.service';

interface FilterHierarchyNode {
  expandable: boolean,
  filter: FilterGroupHierarchyNode,
  name: string,
  level: number
}

@Component({
    selector: 'app-filter-hierarchy',
    imports: [
        MatTree,
        MatTreeNode,
        MatTreeNodeDef,
        MatTreeNodePadding,
        MatIconButton,
        MatIcon,
        MatTreeNodeToggle,
        NgClass
    ],
    templateUrl: './filter-hierarchy.component.html',
    styleUrl: './filter-hierarchy.component.scss'
})
export class FilterHierarchyComponent implements OnInit, OnDestroy, OnChanges{
  // Injections
  private readonly searchExecutor: SearchExecutorService = inject(SearchExecutorService);

  private countData = null;

  @Input() searchContainer: SearchContainer;
  @Input() hierarchicFilterGroup: HierarchicFilterGroup;

  treeControl = new FlatTreeControl<FilterHierarchyNode>(
    node => node.level,
    node => node.expandable
  )

  treeFlattener = new MatTreeFlattener(
    this.transformer,
    node => node.level,
    node => node.expandable,
    node => node.childrenArray
  )

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  ngOnChanges() {
    if (this.hierarchicFilterGroup?.childrenArray) {
      this.dataSource.data = this.sortHierarchyNode(this.hierarchicFilterGroup.childrenArray as unknown[] as HierarchicFilterGroup[]);
    }
  }

  ngOnInit() {
    this.searchExecutor.subscribeToSearchResult(this.searchContainer, this.handleSearchPerformed);
  }

  ngOnDestroy() {
    this.searchExecutor.unSubscribeToSearchResult(this.searchContainer, this.handleSearchPerformed);
  }

  hasChild(_: number, node: any) {
    return node.expandable
  }

  checkNode(node: FilterHierarchyNode, event: MouseEvent) {
    event.stopPropagation();
    const lastSelectedNode = this.hierarchicFilterGroup.lastSelectedNode;

    for (const filter of this.hierarchicFilterGroup.childrenArray) {
      if (filter.facet.id === node.filter.facet.id) {
        if (!lastSelectedNode || lastSelectedNode.facet.id !== node.filter.facet.id) {
          if (lastSelectedNode) {
            lastSelectedNode.open = false;
            lastSelectedNode.facet.selected = false;
          }
          this.hierarchicFilterGroup.lastSelectedNode = node.filter;
          filter.facet.selected = true;
          filter.open = true;
        } else {
          lastSelectedNode.facet.selected = false;
          this.hierarchicFilterGroup.lastSelectedNode = null;
        }
      }

      if (filter.childrenArray && filter.childrenArray.length > 0) {
        for (const child of filter.childrenArray) {
          this.checkNodeChildren(child, node, true);
        }
      }
    }

    this.searchContainer.searchPage = 1;
    this.searchContainer.rows = 50;

    this.searchExecutor.runSearch(this.searchContainer).then();
  }

  getNodeCount(nodeFacet): number {
    if (this.countData) {
      for (const facet of this.countData.items) {
        if (facet.id === nodeFacet.id) {
          return facet.count;
        }
      }

      return 0;
    }

    return nodeFacet.count;
  }

  checkNodeChildren(node: FilterGroupHierarchyNode, checkedNode: FilterHierarchyNode, checked: boolean) {
    const lastSelectedNode = this.hierarchicFilterGroup.lastSelectedNode;

    if (node.facet.id === checkedNode.filter.facet.id) {
      if (!lastSelectedNode || lastSelectedNode.facet.id !== node.facet.id) {
        if (lastSelectedNode) {
          lastSelectedNode.open = false;
          lastSelectedNode.facet.selected = false;
        }
        this.hierarchicFilterGroup.lastSelectedNode = node;
        node.facet.selected = checked;
        node.open = checked;
      } else {
        lastSelectedNode.facet.selected = false;
        this.hierarchicFilterGroup.lastSelectedNode = null;
      }
    }

    if (node.childrenArray && node.childrenArray.length > 0) {
      for (const child of node.childrenArray) {
         this.checkNodeChildren(child, checkedNode, checked);
      }
    }
  }

  private updateCount(facetData) {
    this.countData = null;

    for (const facet of facetData) {
      if (['m_path', 'materialized_paths.m_path'].includes(facet.f_name)) {
        this.countData = facet;
        break;
      }
    }

    if (this.countData === null) {
      this.countData = facetData[1];
    }
  }

  private sortHierarchyNode(data: HierarchicFilterGroup[]) {
    const temp = [...data];

    for (const node of temp) {
      if (node.childrenArray?.length) {
        node.childrenArray = this.sortHierarchyNode(node.childrenArray as unknown[] as HierarchicFilterGroup[])
      }
    }

    return [...temp.sort(this.naturalSort)];
  }

  private readonly naturalSort = (a, b) => {
    return new Intl.Collator('nb-NO', {numeric: true, sensitivity: 'base'}).compare(a.name, b.name);
  }

  private handleSearchPerformed = () => {
    this.updateCount(this.searchContainer.searchResult.facets);
  }

  private transformer (node: FilterGroupHierarchyNode, level: number) {
    return {
      expanded: node.open,
      expandable: !!node.childrenArray && node.childrenArray.length > 0,
      filter: node,
      name: node.facet.shortName,
      level: level
    }
  }
}
